// command-adapter-review-and-controller-rows.ts
//
// The review / testaudit / decision / controller-executed rows of the adapter
// table (review-local-v1 … deploy-v1), split out of command-adapter-table.ts
// to keep it under the 200-line budget (plan 20260924-0205). Data only;
// command-adapter-table.ts appends these AFTER the code-writing rows, so the
// table order (and the order command-adapters.md mirrors) is unchanged.
import {
  type CommandAdapterDefinition,
  READ_ONLY_TOOLS,
  STAGING_WRITE_TOOLS,
} from "./command-adapter-types-and-tool-fragments.js";

export const REVIEW_AND_CONTROLLER_ADAPTER_ROWS: CommandAdapterDefinition[] = [
  {
    id: "review-local-v1",
    kind: "review",
    executor: "claude",
    entryPoint:
      "CLI prompt (run-command-prompt-builder) + kind playbook (skill pr-gatekeeper-local, agent code-reviewer)",
    allowedTools: [
      ...READ_ONLY_TOOLS,
      "Bash(git -C {{WORKTREE}} diff*)",
      "Bash(git -C {{WORKTREE}} log*)",
      "Bash(git -C {{WORKTREE}} show*)",
      // P20: without a Write grant this adapter could not produce the review
      // report its own gate consumes — the session was read-only, so
      // `review-staging/<sha>.json` could never appear and the release flow
      // could only ever answer `waiting_review_report`. Scoped to the attempt
      // staging dir exactly like survey-v1: the report crosses into control
      // root only via the controller (review-report-promotion.ts), never by
      // the worker writing there itself (§9.3).
      ...STAGING_WRITE_TOOLS,
    ],
    maxInternalSubagents: 0,
  },
  {
    // C2 (plan 20260915-1650 phase-03): the SECOND reviewer. Same model, same
    // tools as review-local-v1 — the only thing that differs is what it is
    // told, and that is the entire point: run-command-prompt-builder.ts omits
    // plan_path/phase_path and the `review` kind playbook for this id, so the
    // session judges the diff against the ticket's CONTRACT (outcome,
    // acceptance, coverage_limits) instead of against the implementer's own
    // framing. A reviewer handed the author's narrative inherits the author's
    // blind spot; spec §C2 calls this "context sạch".
    //
    // Reuses `kind: "review"` rather than introducing one: a new kind would
    // touch TicketKindSchema, the dispatch gate guard's bootstrap exemption
    // and the ticket template enum, none of which this phase needs.
    id: "review-clean-v1",
    kind: "review",
    executor: "claude",
    entryPoint:
      "CLI prompt (run-command-prompt-builder) + workflows/autopilot/review-clean.md (agent code-reviewer)",
    allowedTools: [
      ...READ_ONLY_TOOLS,
      "Bash(git -C {{WORKTREE}} diff*)",
      "Bash(git -C {{WORKTREE}} log*)",
      "Bash(git -C {{WORKTREE}} show*)",
      // Same staging-only grant as review-local-v1 (P20's argument applies
      // unchanged): without it the session cannot write the report its own
      // verdict lives in. Scoped — no bare Edit-family name, so a reviewer
      // can never rewrite what it is reviewing.
      ...STAGING_WRITE_TOOLS,
    ],
    maxInternalSubagents: 0,
  },
  {
    // C2: testaudit — "do these tests actually catch anything?". READ-ONLY by
    // decision (user, 2026-09-15), not by omission: the honest version of this
    // check mutates product source and expects the recipe to go red, but no
    // adapter in this project holds a write grant outside
    // `.avatar/attempt-output/**`, and handing one to the adapter whose job is
    // to BREAK things is the widest grant in the table for the weakest reason.
    //
    // What this buys, stated plainly so nobody reads it as more: it finds
    // tests that cannot fail by construction (skipped/only-ed assertions,
    // assertions that never call the changed code, empty test bodies). It does
    // NOT find a test that runs and asserts the wrong thing — that needs real
    // mutation, which is a separate phase with a separate authority question.
    // workflows/autopilot/testaudit.md repeats this limit for the worker.
    id: "testaudit-v1",
    kind: "test",
    executor: "claude",
    entryPoint:
      "CLI prompt (run-command-prompt-builder) + workflows/autopilot/testaudit.md (agent tester)",
    allowedTools: [
      ...READ_ONLY_TOOLS,
      "Bash(git -C {{WORKTREE}} diff*)",
      "Bash(git -C {{WORKTREE}} log*)",
      "Bash(git -C {{WORKTREE}} show*)",
      ...STAGING_WRITE_TOOLS,
    ],
    maxInternalSubagents: 0,
  },
  {
    id: "review-remote-v1",
    kind: "review",
    executor: "claude",
    entryPoint: "Controller (GitLab REST, v1) + worker review artifact via bound skill/agent",
    allowedTools: [
      ...READ_ONLY_TOOLS,
      "Bash(gh pr view*)",
      "Bash(gh pr diff*)",
      "Bash(glab mr view*)",
      "Bash(glab mr diff*)",
    ],
    maxInternalSubagents: 0,
  },
  {
    id: "decision-v1",
    kind: "decision",
    executor: "claude",
    entryPoint:
      "CLI prompt (run-command-prompt-builder) + kind playbook (agent planner; debate-skeptic/debate-refiner khi cần)",
    allowedTools: [...READ_ONLY_TOOLS, ...STAGING_WRITE_TOOLS],
    maxInternalSubagents: 2,
  },
  {
    id: "revert-v1",
    kind: "revert",
    executor: "controller",
    entryPoint: "Controller runs workflows/autopilot/recovery.md recipe (no Claude tools)",
    allowedTools: [],
    maxInternalSubagents: 0,
  },
  {
    id: "deploy-v1",
    kind: "deploy",
    executor: "controller",
    entryPoint: "Controller runs workflows/autopilot/release.md recipe (no Claude tools)",
    allowedTools: [],
    maxInternalSubagents: 0,
  },
];
